import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import { api } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import SponsoredCard from "@/components/SponsoredCard";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const CATEGORIES = ["all", "Face", "Eyes", "Lips", "Fragrance", "Skincare", "Tools"];

// Interleave sponsored ads into products at max 1 per `every` items (1-in-5).
function buildGrid(products, ads, every = 5) {
  if (!ads.length) return products.map((p) => ({ kind: "product", data: p }));
  const items = [];
  let adIdx = 0;
  products.forEach((p, i) => {
    items.push({ kind: "product", data: p });
    // after every (every-1) products, drop one ad -> ratio 1 ad : (every-1) products
    if ((i + 1) % (every - 1) === 0 && adIdx < ads.length) {
      items.push({ kind: "ad", data: ads[adIdx++] });
    }
  });
  return items;
}

export default function Shop() {
  const [params, setParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [ads, setAds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState(params.get("search") || "");
  const [category, setCategory] = useState(params.get("category") || "all");
  const [condition, setCondition] = useState("all");
  const [sort, setSort] = useState("newest");

  usePageMeta(
    "Shop Makeup — New & Used Beauty for Sale | Beauty Trade Club",
    "Browse authentic new and gently-used makeup, skincare, fragrance and tools from verified sellers. Filter by category and condition with escrow-protected checkout.",
    "/shop"
  );

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/products", {
        params: { search: search || undefined, category, condition, sort },
      });
      setProducts(data);
    } finally {
      setLoading(false);
    }
  }, [search, category, condition, sort]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    api.get("/ads/active").then(({ data }) => setAds(data)).catch(() => {});
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    setParams(search ? { search } : {});
    fetchProducts();
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10">
      <h1 className="font-display text-3xl sm:text-4xl font-bold mb-2">Shop the Club</h1>
      <p className="text-neutral-500 mb-8 max-w-3xl">
        Discover authentic new and gently-used beauty from verified sellers. Browse makeup, skincare, fragrance,
        eyeshadow, foundation, blush and more — every listing is posted by a real, manually-approved member, and every
        purchase is protected by escrow until you confirm it arrived as described. Filter by category or condition, read
        honest star reviews, and find your next holy-grail product for less.
      </p>

      <form onSubmit={submitSearch} className="flex items-center bg-neutral-50 border border-neutral-300 rounded-full p-2 mb-6 focus-within:border-[#E8956B] transition-all">
        <Search size={20} className="text-neutral-400 ml-3" />
        <input
          data-testid="shop-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search products, brands or keywords..."
          className="bg-transparent border-none outline-none text-neutral-900 px-4 py-2 w-full placeholder-neutral-400"
        />
        <button data-testid="shop-search-submit" type="submit" className="bg-[#E8956B] hover:bg-[#CE744A] text-white font-bold rounded-full px-6 py-2 transition-all">
          Search
        </button>
      </form>

      <div className="flex flex-wrap gap-3 mb-8">
        <Select value={category} onValueChange={setCategory}>
          <SelectTrigger data-testid="filter-category" className="w-40 bg-neutral-50 border-neutral-300"><SelectValue placeholder="Category" /></SelectTrigger>
          <SelectContent className="bg-white border-neutral-200 text-neutral-900">
            {CATEGORIES.map((c) => <SelectItem key={c} value={c} className="capitalize">{c === "all" ? "All Categories" : c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={condition} onValueChange={setCondition}>
          <SelectTrigger data-testid="filter-condition" className="w-36 bg-neutral-50 border-neutral-300"><SelectValue placeholder="Condition" /></SelectTrigger>
          <SelectContent className="bg-white border-neutral-200 text-neutral-900">
            <SelectItem value="all">All Conditions</SelectItem>
            <SelectItem value="new">New</SelectItem>
            <SelectItem value="swatched">Swatched once</SelectItem>
            <SelectItem value="used">Lightly used</SelectItem>
          </SelectContent>
        </Select>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger data-testid="filter-sort" className="w-40 bg-neutral-50 border-neutral-300"><SelectValue placeholder="Sort" /></SelectTrigger>
          <SelectContent className="bg-white border-neutral-200 text-neutral-900">
            <SelectItem value="newest">Newest</SelectItem>
            <SelectItem value="price_low">Price: Low to High</SelectItem>
            <SelectItem value="price_high">Price: High to Low</SelectItem>
            <SelectItem value="rating">Top Rated</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-neutral-500">Loading products...</p>
      ) : products.length === 0 ? (
        <div className="text-center py-20 text-neutral-500" data-testid="no-products">No products found. Try a different search.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="products-grid">
          {buildGrid(products, ads).map((item) =>
            item.kind === "ad"
              ? <SponsoredCard key={`ad-${item.data.id}`} ad={item.data} />
              : <ProductCard key={item.data.id} product={item.data} />
          )}
        </div>
      )}
    </div>
  );
}
